import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Skeleton,
  Alert,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { itemsApi, Item, ItemStatus } from "../../services/api";
import StatusChip from "../../components/StatusChip";

function StatusTimeline({ changes }: { changes: Item["status_changes"] }) {
  if (!changes || changes.length === 0) return null;

  return (
    <Box>
      {changes.map((change, idx) => (
        <Box
          key={change.id}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            mb: 1.5,
            pl: 1.5,
            borderLeft: "3px solid",
            borderColor: idx === 0 ? "primary.main" : "grey.300",
          }}
        >
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ textTransform: "capitalize" }}>
              {change.from_status
                ? `${change.from_status.replace("_", " ")} → ${change.to_status.replace("_", " ")}`
                : change.to_status.replace("_", " ")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(change.changed_at).toLocaleString()}
            </Typography>
            {change.note && (
              <Typography variant="caption" color="text.secondary" display="block">
                {change.note}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default function ItemDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<ItemStatus>("purchased");
  const [salePrice, setSalePrice] = useState("");
  const [updating, setUpdating] = useState(false);

  const fetchItem = () => {
    if (!id) return;
    setLoading(true);
    itemsApi
      .get(Number(id))
      .then((res) => {
        setItem(res.data);
        setNewStatus(res.data.status);
        setSalePrice(res.data.sale_price || "");
      })
      .catch(() => setError("Failed to load item."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDelete = async () => {
    if (!id) return;
    try {
      await itemsApi.delete(Number(id));
      navigate("/items");
    } catch {
      setError("Failed to delete item.");
    }
  };

  const handleStatusUpdate = async () => {
    if (!id || !item) return;
    setUpdating(true);
    try {
      const data: Record<string, unknown> = { status: newStatus };
      if ((newStatus === "priced" || newStatus === "in_booth" || newStatus === "sold") && salePrice) {
        data.sale_price = salePrice;
      }
      await itemsApi.update(Number(id), data);
      fetchItem();
      setStatusDialogOpen(false);
    } catch {
      setError("Failed to update status.");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={200} />
        <Skeleton variant="rounded" height={150} />
      </Stack>
    );
  }

  if (error || !item) {
    return <Alert severity="error">{error || "Item not found"}</Alert>;
  }

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 0.5 }}>
        <IconButton onClick={() => navigate("/items")} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
          Item Details
        </Typography>
        <IconButton
          color="primary"
          onClick={() => navigate(`/items/${id}/edit`)}
          size="small"
        >
          <EditIcon />
        </IconButton>
        <IconButton color="error" onClick={() => setDeleteDialogOpen(true)} size="small">
          <DeleteIcon />
        </IconButton>
      </Box>

      <Stack spacing={2}>
        {/* Photo */}
        <Card>
          {item.photo ? (
            <CardMedia
              component="img"
              image={item.photo}
              alt={item.description}
              sx={{
                maxHeight: { xs: 220, md: 400 },
                objectFit: "contain",
                backgroundColor: "grey.50",
              }}
            />
          ) : (
            <Box
              sx={{
                height: { xs: 160, md: 300 },
                backgroundColor: "grey.100",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography color="text.secondary" variant="body2">
                No Photo
              </Typography>
            </Box>
          )}
        </Card>

        {/* Info Card */}
        <Card>
          <CardContent sx={{ p: { xs: 2, md: 3 }, "&:last-child": { pb: { xs: 2, md: 3 } } }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5, gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                #{item.id}
              </Typography>
              <StatusChip status={item.status} size="small" />
            </Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ wordBreak: "break-word", mb: 1 }}>
              {item.description}
            </Typography>

            <Divider sx={{ my: 1.5 }} />

            {/* Price & Date Grid - 2x2 compact */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Purchase Price
                </Typography>
                <Typography variant="subtitle1" fontWeight={600}>
                  ${Number(item.purchase_price).toFixed(2)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {item.status === "sold" ? "Sale Price" : "Asking Price"}
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color={item.sale_price ? "success.main" : "text.secondary"}
                >
                  {item.sale_price ? `$${Number(item.sale_price).toFixed(2)}` : "—"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2">
                  {new Date(item.created_at).toLocaleDateString()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Updated
                </Typography>
                <Typography variant="body2">
                  {new Date(item.updated_at).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={() => {
                setNewStatus(item.status);
                setSalePrice(item.sale_price || "");
                setStatusDialogOpen(true);
              }}
            >
              Update Status
            </Button>
          </CardContent>
        </Card>

        {/* Status History */}
        <Card>
          <CardContent sx={{ p: { xs: 2, md: 3 }, "&:last-child": { pb: { xs: 2, md: 3 } } }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Status History
            </Typography>
            <StatusTimeline changes={item.status_changes} />
          </CardContent>
        </Card>
      </Stack>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Item</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete "{item.description}"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Update Status</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Status"
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value as ItemStatus)}
            fullWidth
            sx={{ mt: 1, mb: 2 }}
          >
            <MenuItem value="purchased">Purchased</MenuItem>
            <MenuItem value="priced">Priced</MenuItem>
            <MenuItem value="in_booth">In Booth</MenuItem>
            <MenuItem value="sold">Sold</MenuItem>
          </TextField>

          {(newStatus === "priced" || newStatus === "in_booth" || newStatus === "sold") && (
            <TextField
              label="Sale Price"
              type="number"
              value={salePrice}
              onChange={(e) => setSalePrice(e.target.value)}
              fullWidth
              inputProps={{ step: "0.01", min: "0", inputMode: "decimal" }}
              helperText={newStatus === "sold" ? "Enter the final sale price" : "Set the asking price"}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleStatusUpdate}
            variant="contained"
            disabled={updating || (newStatus === "sold" && !salePrice)}
          >
            {updating ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
